import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { loginSchema } from "@sportscanner/schemas";
import AuthLayout from "@/components/auth-layout";
import { signIn } from "@/lib/auth-client";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const isDisabled = !email.trim() || !password.trim() || isLoading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = loginSchema.safeParse({ email: email.trim(), password });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    try {
      const result = await signIn.email({
        email: parsed.data.email,
        password: parsed.data.password,
        callbackURL: "/",
      });

      if (result?.error) {
        const code = result.error.code;
        if (code === "TOO_MANY_REQUESTS") {
          setErrorCode("TOO_MANY_REQUESTS");
        } else {
          setErrorCode("INVALID_CREDENTIALS");
        }
        return;
      }

      navigate("/");
    } catch {
      setErrorCode("INTERNAL_ERROR");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Heading */}
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-bold text-neutral-800">
            {t("auth.login.heading")}
          </h1>
          <p className="text-sm text-neutral-500">
            {t("auth.login.description")}
          </p>
        </div>

        {/* Error alert */}
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        {/* Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-neutral-600">
              {t("auth.login.emailLabel")}
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t("auth.login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="rounded-lg border-neutral-200 focus:border-primary-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-neutral-600">
              {t("auth.login.passwordLabel")}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("auth.login.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="rounded-lg border-neutral-200 focus:border-primary-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Forgot password */}
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t("auth.login.forgotPassword")}
          </Link>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={isDisabled}
          className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.login.submitting")}
            </>
          ) : (
            t("auth.login.submit")
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
